import React, {useEffect, useState, FC} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {NavLink, useHistory, useLocation} from 'react-router-dom'

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import ClearSharpIcon from '@mui/icons-material/ClearSharp'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MenuSharpIcon from '@mui/icons-material/MenuSharp'
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  DialogContent,
  DialogContentText,
  Hidden,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Theme,
  Typography,
} from '@mui/material'
import {getProductLineColors, PGTypography} from '@progynyglobal/pg-ui-library'
import {PGButton} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import Logo from '@progyny/components/Logo'
import LanguagePickerSheet from '@progyny/components/Navigation/LanguagePickerSheet'
import LanguageSelector from '@progyny/components/Navigation/LanguageSelector'
import MobileNavDropdown from '@progyny/components/Navigation/MobileNavDropdown'
import SwitchProduct from '@progyny/components/Navigation/SwitchProduct'
import useMobileLanguagePicker from '@progyny/components/Navigation/useMobileLanguagePicker'
import {
  useDisablePartnerConsentNotificationMutation,
  useDisableRedesignNotificationMutation,
  useMeQuery,
} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {ProfRoutes} from '@progyny/routes/components/profile'
import {useMeStore} from '@progyny/store/meStore'
import {useNavigationStore} from '@progyny/store/navigationStore'
import {Style} from '@progyny/type/Style'
import {
  FERTILITY,
  MENOPAUSE,
  PREGNANCY_AND_POSTPARTUM,
} from '@progyny/utils/constants'

import {theme} from '@root/theme'

type RenderNotifictionProps = {
  isAcceptedNotification: boolean
  startClaimBtnClassName: string
  onClose: (startClaim?: boolean) => void
}

type StyleProps = {
  bg: string
  fg: string
  border?: string
}
type StyleWithColors = StyleProps & {
  colors?: any
}
type StyleMap = Record<Style, StyleProps>

const useStyles = makeStyles<StyleWithColors>()((theme: Theme, props) => ({
  root: {
    height: theme.palette.other?.constValues?.appHeaderHeight || 64,
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    margin: '16px auto 0px auto',
    maxWidth: '1620px',
    borderRadius: 32,
    zIndex: 150,
    backgroundColor:
      theme.palette.other?.brandColors?.navColor ||
      theme.palette.background.default,
    border: !props.border || props.border === 'none' ? 0 : props.border,
  },
  navContainer: {
    display: 'flex !important',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: theme.palette.other?.constValues?.appHeaderHeight || 64,
  },
  navContent: {
    display: 'flex',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  navLinks: {
    display: 'flex',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    justifyContent: 'flex-start',
    marginLeft: theme.spacing(1),
    '& li a': {
      display: 'block',
      fontWeight: 500,
      padding: theme.spacing(1),
      marginLeft: theme.spacing(1 / 2),
      color: 'inherit',
    },
  },
  activeLink: {
    display: 'inline-block',
    position: 'relative',
    color: `${props.colors.primary} !important`,
  },
  logoImage: {
    fill: props.fg,
    width: '120px',
  },
  logoWrapper: {
    display: 'flex',
    '& a': {
      display: 'flex',
    },
  },
  startClaimBtn: {
    marginLeft: '2rem',
  },
  bookingConsultation: {
    '& button span': {
      '@media screen and (max-width: 1005px)': {
        margin: 0,
      },
    },
  },
  consulatation: {
    '@media screen and (max-width: 1005px)': {
      display: 'none',
    },
  },
  menuAction: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    '& button': {
      minWidth: 'initial',
      justifyContent: 'flex-end',
      '& .MuiSvgIcon-root': {
        fontSize: 28,
      },
    },
  },
  list: {
    minWidth: 250,
  },
}))

const styleMap: StyleMap = {
  blue: {
    bg: 'var(--color-dark-cornflower-blue)',
    fg: 'var(--color-white)',
  },
  'soft-pink': {
    bg: 'var(--color-soft-pink)',
    fg: 'var(--color-dark-cornflower-blue)',
  },
  'soft-blue': {
    bg: 'var(--color-powder-blue)',
    fg: 'var(--color-dark-cornflower-blue)',
  },
  white: {
    bg: 'var(--color-white)',
    fg: 'var(--color-dark-cornflower-blue)',
  },
  seashell: {
    bg: 'var(--color-seashell)',
    fg: 'var(--color-dark-cornflower-blue)',
  },
  'light-coral': {
    bg: 'var(--color-light-coral)',
    fg: 'inherit',
  },
  'dark-grey': {
    bg: 'var(--color-dark-grey)',
    fg: 'var(--color-white)',
  },
  transparent: {
    bg: 'transparent',
    fg: 'var(--color-dark-cornflower-blue)',
  },
}

export const getProductLineLabel = (productLine: string) => {
  switch (productLine) {
    case FERTILITY:
      return 'Fertility'
    case MENOPAUSE:
      return 'Menopause'
    case PREGNANCY_AND_POSTPARTUM:
      return 'Pregnancy & Postpartum'
    default:
      return 'Fertility'
  }
}

// Defined at module scope (rather than inside NavigationMenu) so this isn't
// recreated as a new component type on every NavigationMenu render, which
// would otherwise force React to unmount/remount it instead of reconciling.
const RenderNotifiction: FC<RenderNotifictionProps> = ({
  isAcceptedNotification,
  startClaimBtnClassName,
  onClose,
}) => {
  const {t} = useTranslation()
  return (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        width: 400,
        zIndex: 1300,
        p: 2,
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'row',
          overflowY: 'hidden',
          mb: 2,
          gap: 1,
        }}
      >
        {isAcceptedNotification ? (
          <Avatar sx={{bgcolor: '#DFFCEB', height: 24, width: 24}}>
            <DoneIcon sx={{color: '#66BA47', fontSize: 16}} />
          </Avatar>
        ) : (
          <Avatar sx={{bgcolor: '#FFDEDC', height: 24, width: 24}}>
            <ClearIcon sx={{color: theme.palette.error.main, fontSize: 16}} />
          </Avatar>
        )}
        <DialogContentText
          fontSize={16}
          fontWeight={400}
          color={theme.palette.text.primary}
        >
          {isAcceptedNotification ? (
            <Trans i18nKey='navigation_your_partner_has_approved_partner' />
          ) : (
            <Trans i18nKey='navigation_your_partner_has_declined_partner' />
          )}
        </DialogContentText>
        <CloseIcon sx={{cursor: 'pointer'}} onClick={() => onClose(false)} />
      </DialogContent>
      {isAcceptedNotification && (
        <PGButton
          className={startClaimBtnClassName}
          onClick={() => onClose(true)}
          variant='outlined'
          label={t('navigation_start_claim_request')}
        />
      )}
    </Paper>
  )
}

function NavigationMenu() {
  const hide = useNavigationStore((state) => state.hide)
  const hideMenu = useNavigationStore((state) => state.hideMenu)
  const variant = useNavigationStore((state) => state.variant) ?? 'soft-blue'
  const mergeNav = useNavigationStore((state) => state.mergeNav)
  const history = useHistory()
  const location = useLocation()
  const {t} = useTranslation()
  const {loading: loadingMeQuery, data} = useMeQuery()
  const {productLine, isFertility, isMenopause, isP3} = useProductLine()
  const colors = getProductLineColors(productLine)
  const StyleProps = styleMap[variant]
  const {classes} = useStyles({...StyleProps, colors})
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isMenuOpen, setMenuOpen] = useState(false)
  const languagePicker = useMobileLanguagePicker()
  const [isJourneyPickerOpen, setJourneyPickerOpen] = useState(false)
  const [disableConsentNotification] =
    useDisablePartnerConsentNotificationMutation()
  const user = useMeStore((s) => s.user)
  const loading = useMeStore((s) => s.loading)
  const isSsoLogin = useMeStore((s) => s.isSsoLogin)
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const onboardingData = useMeStore((s) => s.onboardingData)
  const companyHasBudget = useMeStore((s) => s.companyHasBudget)
  const toggleHelpOverlay = useMeStore((s) => s.toggleHelpOverlay)
  const isHelpOverlayOpen = useMeStore((s) => s.isHelpOverlayOpen)
  const [showNewBanner, setShowNewBanner] = useState(
    user?.member?.showRedesignNotification
  )
  const getErrorMessage = useErrorCodes()
  const [disableBannerMutation] = useDisableRedesignNotificationMutation()

  const {navbarItems, routes, getPath} = useRoutes(
    companyHasBudget,
    undefined,
    user?.member?.contractType!,
    productLine
  )
  useEffect(() => {
    if (user?.member?.showRedesignNotification !== undefined) {
      setShowNewBanner(user.member.showRedesignNotification)
    }
  }, [user?.member?.showRedesignNotification])

  const toggleAccountMenu = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setAnchorEl((prv: HTMLElement | null) => {
      return prv === null ? e.currentTarget : null
    })
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const onProfileClick = () => {
    handleMenuClose()
    history.push(getPath(RouteEnum.PROFILE))
  }

  const onExploreJourneyClick = () => {
    handleMenuClose()
    history.push(`${getPath(RouteEnum.PROFILE)}${ProfRoutes.EXPLORE_JOURNEY}`)
  }

  const onLogoutClick = () => {
    handleMenuClose()
    history.push(getPath(RouteEnum.LOGOUT))
  }

  const handleBannerClosure = async () => {
    try {
      const res = await disableBannerMutation()
      const result = res.data?.disableRedesignNotification
      if (result?.success) {
        setShowNewBanner(false)
      } else if (result?.errors) {
        const message = result.errors[0]?.messages[0]
        enqueueSnackbar({
          message: getErrorMessage(message as string),
          variant: 'error',
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleClose = async (startClaim?: boolean) => {
    try {
      const res = await disableConsentNotification()
      const result = res.data?.disablePartnerConsentNotification
      if (result?.success && startClaim) {
        const paRedirectPath = getPath(RouteEnum.PA_REDIRECT)
        history.push({
          pathname: paRedirectPath,
          state:
            location.pathname !== paRedirectPath
              ? {returnTo: `${location.pathname}${location.search}`}
              : undefined,
        })
      } else if (result?.errors) {
        const message = result.errors[0]?.messages[0]
        enqueueSnackbar({
          message: getErrorMessage(message as string),
          variant: 'error',
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const resetNavbarAndRemoveOverlay = () => {
    if (routes.HOME.withNavigation) {
      mergeNav(routes.HOME.withNavigation)
    }
    if (isHelpOverlayOpen) {
      toggleHelpOverlay(false)
    }
  }

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    resetNavbarAndRemoveOverlay()
    history.push(getPath(RouteEnum.HOME))
  }

  const openLanguagePicker = () => {
    setMenuOpen(false)
    languagePicker.openPicker()
  }

  const openJourneyPicker = () => {
    setMenuOpen(false)
    setJourneyPickerOpen(true)
  }

  const isAprylNavigator = location.pathname.includes(
    getPath(RouteEnum.APRYL_NAVIGATOR)
  )

  const checkOnboardingDone = (): boolean =>
    (isFertility && onboardingData?.familyFormingJourney !== 'unset') ||
    (isMenopause && onboardingData?.menopauseSymptoms !== 'unset') ||
    (isP3 && !!(onboardingData?.deliveryDate || onboardingData?.dueDate))

  return (
    <>
      {!hide && (
        <nav className={!hideMenu ? classes.root : ''} id={'navbar-wrapper'}>
          {user?.member?.partnerConsentStatus === 'accepted' &&
            user?.member?.showPartnerConsentNotification && (
              <RenderNotifiction
                isAcceptedNotification={true}
                startClaimBtnClassName={classes.startClaimBtn}
                onClose={handleClose}
              />
            )}
          {user?.member?.partnerConsentStatus === 'rejected' &&
            user?.member?.showPartnerConsentNotification && (
              <RenderNotifiction
                isAcceptedNotification={false}
                startClaimBtnClassName={classes.startClaimBtn}
                onClose={handleClose}
              />
            )}

          <Container
            maxWidth={isAprylNavigator ? false : 'xl'}
            classes={{root: classes.navContainer}}
          >
            <Box className={classes.navContent}>
              <Box className={classes.logoWrapper}>
                {isLoggedIn && checkOnboardingDone() ? (
                  <Box display='flex' justifyContent='center'>
                    <a
                      href={getPath(RouteEnum.HOME)}
                      aria-label={'Go to homepage'}
                      onClick={handleLogoClick}
                      style={{cursor: 'pointer'}}
                    >
                      <Logo className={classes.logoImage} />
                    </a>
                  </Box>
                ) : (
                  <Logo className={classes.logoImage} />
                )}
              </Box>
              {hideMenu && (
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <LanguageSelector onWhite />
                </Box>
              )}
              {/* links */}
              {!hideMenu && (
                <>
                  <Hidden mdDown>
                    {!loading && !loadingMeQuery && (
                      <ul className={classes.navLinks}>
                        {navbarItems
                          .filter(
                            (item) => item.path !== getPath(RouteEnum.HOME)
                          )
                          .map((item, idx) => (
                            <li key={`navbar-lg-${idx}`}>
                              <NavLink
                                activeClassName={classes.activeLink}
                                to={item.path}
                              >
                                {t(item.title)}
                              </NavLink>
                            </li>
                          ))}
                      </ul>
                    )}
                  </Hidden>
                  {/* book */}
                  <Hidden mdDown>
                    <Box
                      className={classes.bookingConsultation}
                      sx={{display: 'flex', alignItems: 'center', gap: 2}}
                    >
                      {data?.languages && data?.languages?.length > 1 && (
                        <LanguageSelector />
                      )}
                      {/* user */}

                      <Box>
                        <Button
                          disableRipple
                          onClick={toggleAccountMenu}
                          aria-label={t('navigation_open_user_menu')}
                          data-testid='navigation-user-menu'
                          sx={{
                            color: theme.palette.other.brandColors.primary,
                            borderRadius: '50%',
                            minWidth: 0,
                            padding: '6px',
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          <AccountCircleOutlinedIcon
                            sx={{
                              color: '#251F1F',
                              fontSize: 22,
                              marginRight: 1,
                            }}
                          />
                          <PGTypography variant='subheader' color='#251F1F'>
                            {t('navigation_my_account')}
                          </PGTypography>
                          <KeyboardArrowDownIcon
                            sx={{
                              color: '#251F1F',
                              fontSize: 22,
                            }}
                          />
                        </Button>
                      </Box>
                      {showNewBanner && (
                        <Box
                          padding={4}
                          width={492}
                          sx={{
                            backgroundColor: theme.palette.tertiary.main,
                            mb: 1,
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            borderRadius: 2,
                          }}
                        >
                          <Box
                            display={'flex'}
                            flexDirection={'row'}
                            justifyContent={'space-between'}
                            alignItems={'center'}
                          >
                            <Typography
                              textAlign={'left'}
                              fontSize={16}
                              fontWeight={600}
                              color={theme.palette.other.brandColors.primary}
                            >
                              <Trans i18nKey='navigation_welcome_to_new_progyny_member' />
                            </Typography>
                            <ClearIcon
                              onClick={handleBannerClosure}
                              sx={{
                                position: 'absolute',
                                right: 8,
                                height: 21,
                                width: 21,
                                color:
                                  theme.palette.other.brandColors.lightGray,
                                cursor: 'pointer',
                              }}
                            />
                          </Box>
                          <Typography
                            mt={1}
                            fontSize={16}
                            fontWeight={400}
                            textAlign={'left'}
                          >
                            <Trans i18nKey='navigation_we_ve_reimagined_platform_with' />
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Hidden>

                  {/* menu button */}
                  <Hidden mdUp>
                    <Box className={classes.menuAction}>
                      <Button onClick={() => setMenuOpen((v) => !v)}>
                        {isMenuOpen ? (
                          <ClearSharpIcon color={'primary'} />
                        ) : (
                          <MenuSharpIcon color={'primary'} />
                        )}
                      </Button>
                    </Box>
                  </Hidden>
                </>
              )}
            </Box>
          </Container>
        </nav>
      )}
      {/* menu open */}
      <Hidden mdUp>
        <MobileNavDropdown
          isOpen={isMenuOpen}
          onClose={() => setMenuOpen(false)}
          languageName={languagePicker.languageName}
          languageLoading={languagePicker.languageLoading}
          onOpenLanguagePicker={openLanguagePicker}
          onOpenJourneyPicker={openJourneyPicker}
        />
      </Hidden>
      <Hidden mdUp>
        <LanguagePickerSheet
          open={languagePicker.isPickerOpen}
          onClose={languagePicker.closePicker}
          languages={languagePicker.languages}
          selectedCode={languagePicker.language}
          onSelect={languagePicker.handleSelect}
        />
      </Hidden>
      <Hidden mdUp>
        <SwitchProduct
          variant='mobile'
          open={isJourneyPickerOpen}
          onClose={() => setJourneyPickerOpen(false)}
        />
      </Hidden>
      {/* menu account  */}
      <Popover
        disableScrollLock
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 3,
          },
        }}
      >
        <List className={classes.list}>
          <ListItemButton sx={{mt: 1}} onClick={onProfileClick}>
            <ListItemText primary={t('user_profile')} />
          </ListItemButton>
          <Divider sx={{mx: 2}} />
          {user?.member?.previouslySelectedJourneys?.length! > 0 ? (
            <SwitchProduct />
          ) : null}
          <Divider sx={{mx: 2}} />
          {user?.member?.availableProductLines?.length! > 1 ? (
            <ListItemButton
              onClick={onExploreJourneyClick}
              data-testid='navigation-manage-journeys'
            >
              <ListItemText primary={t('navigation_manage_my_journeys')} />
            </ListItemButton>
          ) : null}

          <Divider sx={{mx: 2}} />
          {!isSsoLogin && (
            <ListItemButton onClick={onLogoutClick}>
              <ListItemText primary={t('user_logout')} />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </>
  )
}

export const Navigation = React.memo(NavigationMenu)
